import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Plus, MapPin, Layers, ShieldCheck, AlertTriangle,
  Loader2, Trash2, Search, X, Home, Pencil, FileDown, Map as MapIcon,
  Users, MessageSquare, HandCoins, FileText, Hexagon, FileSpreadsheet, ScrollText,
  UserCog, LocateFixed, RefreshCw, WifiOff,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import ParcelMap from "@/components/ParcelMap";
import AdminList from "@/components/AdminList";
import BoundaryEditor from "@/components/BoundaryEditor";
import AgentsPanel from "@/components/AgentsPanel";
import JournalPanel from "@/components/JournalPanel";

const OFFLINE_KEY = "heritage_offline_parcels";
const getQueue = () => { try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || "[]"); } catch { return []; } };
const setQueue = (q) => localStorage.setItem(OFFLINE_KEY, JSON.stringify(q));

const STATUTS = {
  "sécurisé": { c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  "en litige": { c: "text-red-400 border-red-500/40 bg-red-500/10" },
  "en cours": { c: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("cadastre");
  const isAdmin = user?.role === "admin";

  const doLogout = async () => { await logout(); navigate("/"); };

  const tabs = [
    { key: "cadastre", label: "Cadastre", icon: Layers, admin: false },
    { key: "adhesions", label: "Adhésions", icon: Users, admin: true },
    { key: "messages", label: "Messages", icon: MessageSquare, admin: true },
    { key: "dons", label: "Dons", icon: HandCoins, admin: true },
    { key: "agents", label: "Agents", icon: UserCog, admin: true },
    { key: "journal", label: "Journal", icon: ScrollText, admin: true },
  ].filter((t) => !t.admin || isAdmin);

  return (
    <div className="dark min-h-screen bg-background text-foreground font-body">
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="ONG HERITAGES" className="w-9 h-9 rounded-full object-cover ring-1 ring-border" />
            <div className="leading-none">
              <p className="font-heading text-lg text-foreground">Registre Foncier</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">ONG Heritages</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">
              {user?.name}{isAdmin && <span className="ml-2 text-secondary">• Admin</span>}
            </span>
            <button data-testid="nav-home-btn" onClick={() => navigate("/")}
              className="grid place-items-center w-9 h-9 rounded-full border border-border hover:border-secondary transition-colors">
              <Home className="w-4 h-4" />
            </button>
            <button data-testid="logout-btn" onClick={doLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:border-secondary text-sm transition-colors">
              <LogOut className="w-4 h-4" /> Sortir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setView(t.key)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border transition-colors ${
                view === t.key ? "bg-secondary text-secondary-foreground border-secondary" : "border-border text-muted-foreground hover:border-secondary"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {view === "cadastre" && <CadastreView />}

        {view === "adhesions" && (
          <AdminSection title="Adhésions & engagements" subtitle="Demandes d'adhésion, bénévolat et partenariat reçues."
            action={<ExportButton endpoint="/admin/memberships/export" filename="ong_heritages_adhesions.xlsx" label="Exporter Excel" />}>
            <AdminList endpoint="/admin/memberships" testid="adhesions-list"
              empty="Aucune demande pour le moment."
              columns={[
                { key: "nom", label: "Nom", w: "1.4fr" },
                { key: "email", label: "Email", w: "1.6fr", muted: true },
                { key: "telephone", label: "Téléphone", w: "1.2fr", muted: true },
                { key: "localite", label: "Localité", w: "1.2fr", muted: true },
                { key: "type", label: "Type", w: "1fr", render: (r) => <span className="text-secondary capitalize">{r.type}</span> },
                { key: "created_at", label: "Reçu le", w: "1fr", muted: true },
              ]} />
          </AdminSection>
        )}

        {view === "messages" && (
          <AdminSection title="Messages de contact" subtitle="Messages envoyés via le formulaire de contact.">
            <AdminList endpoint="/admin/contacts" testid="messages-list"
              empty="Aucun message pour le moment."
              columns={[
                { key: "nom", label: "Nom", w: "1.2fr" },
                { key: "email", label: "Email", w: "1.5fr", muted: true },
                { key: "sujet", label: "Sujet", w: "1.3fr" },
                { key: "message", label: "Message", w: "2.2fr", muted: true },
                { key: "created_at", label: "Reçu le", w: "1fr", muted: true },
              ]} />
          </AdminSection>
        )}

        {view === "dons" && (
          <AdminSection title="Dons à la cause" subtitle="Engagements de dons enregistrés via le site."
            action={<ExportButton endpoint="/admin/donations/export" filename="ong_heritages_dons.xlsx" label="Exporter Excel" />}>
            <AdminList endpoint="/admin/donations" testid="dons-list"
              empty="Aucun don pour le moment."
              columns={[
                { key: "nom", label: "Donateur", w: "1.3fr" },
                { key: "email", label: "Email", w: "1.6fr", muted: true },
                { key: "telephone", label: "Téléphone", w: "1.2fr", muted: true },
                { key: "montant", label: "Montant", w: "1fr", render: (r) => <span className="text-secondary font-medium">{r.montant} FCFA</span> },
                { key: "frequence", label: "Fréquence", w: "1fr", render: (r) => <span className="capitalize">{r.frequence}</span> },
                { key: "moyen", label: "Moyen", w: "1.1fr", muted: true, render: (r) => <span className="capitalize">{String(r.moyen).replace("_", " ")}</span> },
                { key: "created_at", label: "Reçu le", w: "0.9fr", muted: true },
                { key: "recu", label: "Reçu", w: "0.6fr", render: (r) => <ReceiptButton donation={r} /> },
              ]} />
          </AdminSection>
        )}
        {view === "agents" && isAdmin && <AgentsPanel currentEmail={user?.email} />}
        {view === "journal" && isAdmin && <JournalPanel />}
      </main>
    </div>
  );
}

function ReceiptButton({ donation }) {
  const download = async () => {
    try {
      const res = await api.get(`/donations/${donation.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `recu_don_${donation.id.slice(-6).toUpperCase()}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Reçu téléchargé");
    } catch { toast.error("Impossible de générer le reçu"); }
  };
  return (
    <button data-testid="receipt-btn" onClick={download} title="Reçu PDF"
      className="grid place-items-center w-8 h-8 rounded-lg hover:bg-secondary/15 hover:text-secondary text-muted-foreground transition-colors">
      <FileText className="w-4 h-4" />
    </button>
  );
}

function ExportButton({ endpoint, filename, label }) {
  const [loading, setLoading] = useState(false);
  const dl = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch { toast.error("Échec de l'export"); }
    finally { setLoading(false); }
  };
  return (
    <button data-testid="export-btn" onClick={dl} disabled={loading}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary text-secondary-foreground text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} {label}
    </button>
  );
}

function AdminSection({ title, subtitle, action, children }) {
  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl lg:text-5xl text-foreground">{title}</h1>
          <p className="text-muted-foreground mt-1">{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function CadastreView() {
  const [parcels, setParcels] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("tous");
  const [showMap, setShowMap] = useState(true);
  const [queued, setQueued] = useState(getQueue().length);
  const [online, setOnline] = useState(navigator.onLine);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/parcels"), api.get("/parcels/stats")]);
      setParcels(p.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Impossible de charger le registre");
    } finally { setLoading(false); }
  };

  const syncOffline = async () => {
    const q = getQueue();
    if (!q.length) return;
    const remaining = [];
    let ok = 0;
    for (const item of q) {
      try { await api.post("/parcels", item); ok++; }
      catch { remaining.push(item); }
    }
    setQueue(remaining);
    setQueued(remaining.length);
    if (ok) { toast.success(`${ok} parcelle(s) synchronisée(s)`); load(); }
    if (remaining.length) toast.error(`${remaining.length} parcelle(s) en attente`);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const goOnline = () => { setOnline(true); syncOffline(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const refreshQueue = () => setQueued(getQueue().length);

  const del = async (id) => {
    try {
      await api.delete(`/parcels/${id}`);
      toast.success("Parcelle supprimée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadCert = async (p) => {
    try {
      const res = await api.get(`/parcels/${p.id}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `certificat_${p.reference}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificat téléchargé");
    } catch (e) {
      toast.error("Certificat disponible uniquement pour les parcelles sécurisées");
    }
  };

  const downloadFiche = async (p) => {
    try {
      const res = await api.get(`/parcels/${p.id}/fiche`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fiche_${p.reference}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Fiche parcellaire téléchargée");
    } catch (e) {
      toast.error("Impossible de générer la fiche");
    }
  };

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); };

  const filtered = useMemo(() => {
    return parcels.filter((p) => {
      const matchQ = [p.reference, p.proprietaire, p.localite, p.region]
        .join(" ").toLowerCase().includes(query.toLowerCase());
      const matchF = filter === "tous" || p.statut === filter;
      return matchQ && matchF;
    });
  }, [parcels, query, filter]);

  const cards = [
    { l: "Parcelles enregistrées", v: stats?.total ?? "—", icon: Layers },
    { l: "Superficie totale (ha)", v: stats?.superficie ?? "—", icon: MapPin },
    { l: "Titres sécurisés", v: stats?.securise ?? "—", icon: ShieldCheck },
    { l: "En litige", v: stats?.litige ?? "—", icon: AlertTriangle },
  ];

  return (
    <>
      {(!online || queued > 0) && (
        <div data-testid="offline-banner" className={`mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border px-5 py-4 ${
          online ? "border-amber-500/40 bg-amber-500/10" : "border-red-500/40 bg-red-500/10"
        }`}>
          <div className="flex items-center gap-3 text-sm">
            <WifiOff className={`w-5 h-5 ${online ? "text-amber-400" : "text-red-400"}`} />
            <span className="text-foreground">
              {online
                ? `${queued} parcelle(s) enregistrée(s) hors-ligne, prêtes à être synchronisées.`
                : "Vous êtes hors-ligne. Les nouvelles parcelles seront sauvegardées localement puis synchronisées."}
              {online && queued > 0 ? "" : ""}
            </span>
          </div>
          {queued > 0 && (
            <button data-testid="sync-btn" onClick={syncOffline} disabled={!online}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-50">
              <RefreshCw className="w-4 h-4" /> Synchroniser ({queued})
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl lg:text-5xl text-foreground">Cadastre communautaire</h1>
          <p className="text-muted-foreground mt-1">Suivi, cartographie et sécurisation des parcelles rurales.</p>
        </div>
        <button data-testid="add-parcel-btn" onClick={openNew}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-medium hover:-translate-y-0.5 transition-transform">
          <Plus className="w-5 h-5" /> Enregistrer une parcelle
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-xl overflow-hidden mb-8">
        {cards.map((c) => (
          <div key={c.l} data-testid="stat-card" className="bg-card p-6">
            <c.icon className="w-5 h-5 text-secondary mb-4" />
            <div className="font-heading text-3xl lg:text-4xl text-foreground">{c.v}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{c.l}</div>
          </div>
        ))}
      </div>

      <div className="mb-8">
        <button data-testid="toggle-map-btn" onClick={() => setShowMap((m) => !m)}
          className="inline-flex items-center gap-2 mb-3 text-sm text-muted-foreground hover:text-secondary transition-colors">
          <MapIcon className="w-4 h-4" /> {showMap ? "Masquer la carte" : "Afficher la carte"}
        </button>
        {showMap && <ParcelMap parcels={parcels} />}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher réf., propriétaire, localité…"
            className="w-full bg-card border border-border rounded-lg pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-secondary" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["tous", "sécurisé", "en cours", "en litige"].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                filter === s ? "bg-secondary text-secondary-foreground border-secondary" : "border-border text-muted-foreground hover:border-secondary"
              }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 bg-card text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border">
          <div className="col-span-2">Référence</div>
          <div className="col-span-2">Propriétaire</div>
          <div className="col-span-2">Localité</div>
          <div className="col-span-1">Région</div>
          <div className="col-span-1">Sup. (ha)</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-secondary" /></div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="p-16 text-center text-muted-foreground">
            Aucune parcelle. Commencez par en enregistrer une.
          </div>
        ) : (
          filtered.map((p, i) => (
            <motion.div key={p.id} data-testid="parcel-row"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
              className="grid grid-cols-2 md:grid-cols-12 gap-2 px-5 py-4 border-b border-border last:border-0 hover:bg-card/60 transition-colors items-center">
              <div className="md:col-span-2 font-mono text-sm text-secondary">{p.reference}</div>
              <div className="md:col-span-2 text-sm">
                {p.proprietaire}
                {p.created_by && <span className="block text-[10px] text-muted-foreground">saisi par {p.created_by}</span>}
              </div>
              <div className="md:col-span-2 text-sm text-muted-foreground">{p.localite}</div>
              <div className="md:col-span-1 text-sm text-muted-foreground">{p.region}</div>
              <div className="md:col-span-1 text-sm">{p.superficie}</div>
              <div className="md:col-span-2">
                <span className={`inline-block px-3 py-1 rounded-full text-xs border ${STATUTS[p.statut]?.c || "text-muted-foreground border-border"}`}>
                  {p.statut}
                </span>
              </div>
              <div className="md:col-span-2 flex justify-end gap-1">
                {p.statut === "sécurisé" && (
                  <button data-testid="cert-parcel-btn" title="Certificat PDF" onClick={() => downloadCert(p)}
                    className="grid place-items-center w-8 h-8 rounded-lg hover:bg-secondary/15 hover:text-secondary text-muted-foreground transition-colors">
                    <FileDown className="w-4 h-4" />
                  </button>
                )}
                <button data-testid="fiche-parcel-btn" title="Fiche parcellaire (PDF)" onClick={() => downloadFiche(p)}
                  className="grid place-items-center w-8 h-8 rounded-lg hover:bg-secondary/15 hover:text-secondary text-muted-foreground transition-colors">
                  <ScrollText className="w-4 h-4" />
                </button>
                <button data-testid="draw-parcel-btn" title="Tracer les limites" onClick={() => setDrawing(p)}
                  className={`grid place-items-center w-8 h-8 rounded-lg hover:bg-secondary/15 hover:text-secondary transition-colors ${
                    Array.isArray(p.boundary) && p.boundary.length >= 3 ? "text-secondary" : "text-muted-foreground"
                  }`}>
                  <Hexagon className="w-4 h-4" />
                </button>
                <button data-testid="edit-parcel-btn" title="Modifier" onClick={() => openEdit(p)}
                  className="grid place-items-center w-8 h-8 rounded-lg hover:bg-secondary/15 hover:text-secondary text-muted-foreground transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid="delete-parcel-btn" title="Supprimer" onClick={() => del(p.id)}
                  className="grid place-items-center w-8 h-8 rounded-lg hover:bg-red-500/10 hover:text-red-400 text-muted-foreground transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {showForm && (
        <ParcelForm parcel={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); refreshQueue(); }} />
      )}
      {drawing && (
        <BoundaryEditor parcel={drawing} onClose={() => setDrawing(null)} onSaved={() => { setDrawing(null); load(); }} />
      )}
    </>
  );
}

function ParcelForm({ parcel, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [lat, setLat] = useState(parcel?.latitude ?? "");
  const [lng, setLng] = useState(parcel?.longitude ?? "");
  const [geoLoading, setGeoLoading] = useState(false);
  const isEdit = Boolean(parcel);

  const locate = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée par cet appareil"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setGeoLoading(false);
        toast.success("Position GPS capturée");
      },
      () => { setGeoLoading(false); toast.error("Impossible d'obtenir la position — autorisez le GPS"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      reference: f.get("reference"),
      proprietaire: f.get("proprietaire"),
      localite: f.get("localite"),
      region: f.get("region"),
      superficie: parseFloat(f.get("superficie")) || 0,
      usage: f.get("usage"),
      statut: f.get("statut"),
      latitude: lat !== "" ? parseFloat(lat) : null,
      longitude: lng !== "" ? parseFloat(lng) : null,
      boundary: parcel?.boundary ?? null,
      notes: f.get("notes"),
    };
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/parcels/${parcel.id}`, payload);
        toast.success("Parcelle mise à jour");
      } else {
        await api.post("/parcels", payload);
        toast.success("Parcelle enregistrée");
      }
      onSaved();
    } catch (err) {
      if (!isEdit && (!navigator.onLine || !err.response)) {
        const q = getQueue();
        q.push(payload);
        setQueue(q);
        toast.success("Enregistré hors-ligne — sera synchronisé au retour du réseau");
        onSaved();
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally { setLoading(false); }
  };

  const input = "w-full bg-background border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-secondary";
  const v = (k, d = "") => (parcel && parcel[k] != null ? parcel[k] : d);

  return (
    <div className="dark fixed inset-0 z-[1100] grid place-items-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-2xl bg-card border border-border rounded-2xl p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl text-foreground">{isEdit ? "Modifier la parcelle" : "Nouvelle parcelle"}</h2>
          <button data-testid="close-form-btn" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={submit} data-testid="parcel-form" className="grid sm:grid-cols-2 gap-5">
          <input name="reference" required defaultValue={v("reference")} placeholder="Référence (ex: FONC-2026-001)" data-testid="parcel-reference" className={input} />
          <input name="proprietaire" required defaultValue={v("proprietaire")} placeholder="Propriétaire / Famille" data-testid="parcel-proprietaire" className={input} />
          <input name="localite" required defaultValue={v("localite")} placeholder="Localité / Village" data-testid="parcel-localite" className={input} />
          <input name="region" required defaultValue={v("region")} placeholder="Région" data-testid="parcel-region" className={input} />
          <input name="superficie" type="number" step="0.01" required defaultValue={v("superficie")} placeholder="Superficie (ha)" data-testid="parcel-superficie" className={input} />
          <select name="usage" defaultValue={v("usage", "agricole")} data-testid="parcel-usage" className={input}>
            <option value="agricole">Agricole</option>
            <option value="pastoral">Pastoral</option>
            <option value="forestier">Forestier</option>
            <option value="habitat">Habitat</option>
          </select>
          <select name="statut" defaultValue={v("statut", "sécurisé")} data-testid="parcel-statut" className={input}>
            <option value="sécurisé">Sécurisé</option>
            <option value="en cours">En cours</option>
            <option value="en litige">En litige</option>
          </select>
          <input name="notes" defaultValue={v("notes")} placeholder="Notes (facultatif)" data-testid="parcel-notes" className={input} />
          <button type="button" onClick={locate} data-testid="geo-locate-btn"
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 py-3 rounded-lg border border-secondary/50 text-secondary text-sm font-medium hover:bg-secondary/10 transition-colors">
            {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} Ma position (GPS du téléphone)
          </button>
          <input name="latitude" type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Latitude GPS (ex: 7.69)" data-testid="parcel-latitude" className={input} />
          <input name="longitude" type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="Longitude GPS (ex: -5.03)" data-testid="parcel-longitude" className={input} />
          <button type="submit" disabled={loading} data-testid="parcel-submit"
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 py-4 rounded-full bg-secondary text-secondary-foreground font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} {isEdit ? "Mettre à jour" : "Enregistrer"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
